"use client";

import React, { useState, useEffect } from "react";
import { MapPin, Clock, Phone, Mail, Map as MapIcon, Globe, Building2 } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
const locations = [{
  id: "hq",
  city: "紅磡 (總部)",
  country: "九龍",
  type: "營運總部",
  address: "九龍九龍城區紅磡馬頭圍道37號紅磡廣場1樓98A鋪",
  phone: "5343 0380",
  email: "info@prospect-care.com",
  timezone: "Asia/Hong_Kong",
  top: "48%",
  left: "55%"
}, {
  id: "hki",
  city: "中環",
  country: "港島區",
  type: "港島服務中心",
  address: "香港中環德輔道中區域服務點",
  phone: "5343 0380",
  email: "hki@prospect-care.com",
  timezone: "Asia/Hong_Kong",
  top: "62%",
  left: "52%"
}, {
  id: "st",
  city: "沙田",
  country: "新界東",
  type: "新界東服務中心",
  address: "新界沙田車站圍區域服務點",
  phone: "5343 0380",
  email: "nte@prospect-care.com",
  timezone: "Asia/Hong_Kong",
  top: "32%",
  left: "62%"
}, {
  id: "tm",
  city: "屯門",
  country: "新界西",
  type: "新界西服務中心",
  address: "新界屯門屯喜路區域服務點",
  phone: "5343 0380",
  email: "ntw@prospect-care.com",
  timezone: "Asia/Hong_Kong",
  top: "38%",
  left: "35%"
}];
function LocalTime({
  timezone
}: {
  timezone: string;
}) {
  const [time, setTime] = useState<string>("");
  useEffect(() => {
    const updateTime = () => {
      try {
        const formatter = new Intl.DateTimeFormat("en-US", {
          timeZone: timezone,
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false
        });
        setTime(formatter.format(new Date()));
      } catch (e) {
        setTime("00:00:00");
      }
    };
    updateTime();
    const intervalId = setInterval(updateTime, 1000);
    return () => clearInterval(intervalId);
  }, [timezone]);
  return <span className="font-mono text-primary tracking-widest text-lg">
      {time || "00:00:00"}
    </span>;
}
export default function GlobalPresence() {
  return <section data-section-id="128536" id="服務區域" className="bg-background text-foreground relative w-full overflow-hidden py-24 lg:py-32">
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#6d28d908_1px,transparent_1px),linear-gradient(to_bottom,#6d28d908_1px,transparent_1px)] bg-[size:24px_24px]"></div>
      
      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div className="mb-16 flex flex-col items-center text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-sm text-primary">
            <Globe className="size-4" />
            <span>全港服務網絡</span>
          </div>
          <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl lg:text-6xl">
            專業醫護 <span className="text-primary">遍佈全港</span>
          </h2>
          <p className="mt-4 max-w-[700px] text-muted-foreground md:text-xl">
            我們的專業團隊分佈於香港各大區域，確保為每個家庭提供即時、專業且充滿關懷的居家護理與復康支援。
          </p>
        </div>

        <div className="relative mx-auto aspect-[16/9] w-full max-w-6xl overflow-hidden rounded-2xl border border-border bg-white shadow-2xl shadow-primary/10">
          {}
          <svg viewBox="0 0 1000 500" className="absolute inset-0 h-full w-full opacity-60" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
            <path d="M150,100 Q200,50 300,80 T450,120 T600,90 T750,150 T850,100 Q900,150 880,250 T800,400 T650,350 T500,450 T350,380 T200,420 T100,300 Q50,200 150,100 Z" fill="none" stroke="currentColor" strokeWidth="1" className="text-primary/10" strokeDasharray="4 4" />
            <path d="M250,150 Q300,120 350,180 T450,200 T550,160 T650,220 T750,180 Q800,250 750,300 T600,350 T450,300 T300,350 T200,280 Q180,200 250,150 Z" fill="currentColor" className="text-primary/5" />
             <path d="M100,50 Q150,20 200,60 T300,40 T400,80 T500,50 T600,90 T700,60 T800,100 T900,70 Q950,120 920,180 T850,250 T900,320 T820,400 T700,450 T550,400 T400,460 T250,420 T150,480 T50,400 T80,300 T20,200 Q50,100 100,50 Z" fill="currentColor" className="text-secondary/20" />
          </svg>

          {}
          <div className="absolute inset-0 bg-gradient-to-t from-white/40 via-transparent to-transparent"></div>

          {}
          {locations.map((loc, i) => <Sheet key={loc.id}>
              <SheetTrigger asChild data-index={i}>
                <div className="group absolute flex size-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center focus:outline-none cursor-pointer" style={{
              top: loc.top,
              left: loc.left
            }}>
                  {}
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-40 group-hover:opacity-80"></span>
                  {}
                  <span className="relative inline-flex size-3 rounded-full bg-primary shadow-[0_0_15px_rgba(109,40,217,0.5)] transition-transform duration-300 group-hover:scale-150"></span>
                  
                  {}
                  <span className="absolute top-full mt-2 hidden whitespace-nowrap rounded border border-border/50 bg-white/90 px-2 py-1 text-xs font-medium text-foreground backdrop-blur-sm transition-all group-hover:block md:block md:opacity-0 md:group-hover:opacity-100 shadow-sm">
                    {loc.city}
                  </span>
                </div>
              </SheetTrigger>
              
              <SheetContent className="border-l-border/50 bg-white/95 text-foreground backdrop-blur-xl sm:max-w-md">
                <SheetHeader className="mb-8 border-b border-border/50 pb-6 text-left">
                  <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                    <Building2 className="size-3" />
                    {loc.type}
                  </div>
                  <SheetTitle className="text-3xl font-bold tracking-tight text-primary">
                    {loc.city}, {loc.country}
                  </SheetTitle>
                  <SheetDescription className="text-muted-foreground">
                    憬芯醫療區域服務中心及營運樞紐，為鄰近社區提供專業護理。
                  </SheetDescription>
                </SheetHeader>

                <div className="grid gap-8">
                  {}
                  <div className="rounded-lg border border-border/50 bg-secondary/30 p-4">
                    <div className="mb-2 flex items-center gap-2 text-sm font-medium text-muted-foreground">
                      <Clock className="size-4" />
                      當地服務時間
                    </div>
                    <LocalTime timezone={loc.timezone} />
                  </div>

                  {}
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-secondary text-primary">
                        <MapIcon className="size-5" />
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-muted-foreground">地址</h4>
                        <p className="mt-1 text-sm leading-relaxed text-foreground">
                          {loc.address}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-secondary text-primary">
                        <Phone className="size-5" />
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-muted-foreground">服務專線</h4>
                        <p className="mt-1 font-mono text-sm text-foreground">
                          {loc.phone}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-secondary text-primary">
                        <Mail className="size-5" />
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-muted-foreground">電子郵件</h4>
                        <p className="mt-1 text-sm text-foreground">
                          {loc.email}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 pt-6 border-t border-border/50">
                    <Button className="w-full gap-2 bg-primary hover:bg-primary/90 text-white" variant="default">
                      <MapPin className="size-4" />
                      在地圖中開啟
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>)}
        </div>
      </div>
    </section>;
}